import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { EquipmentResponse, EquipmentType } from 'store/equipments/equipments.types'
import { useListEquipmentsQuery } from 'store/equipments/equipments.api'
import { ACCENT_BLUE } from 'utils/brand-colors'

const GREEN = '#16A34A'
const BORDER = '#E5E7EB'
const MUTED = '#6B7280'

const formatType = (type: EquipmentType) =>
  type === 'TRACTOR' ? 'Tractor' : 'Trailer'

const FleetEquipmentPanel = () => {
  const { data: equipments = [], isLoading } = useListEquipmentsQuery()
  const [tab, setTab] = useState<EquipmentType>('TRACTOR')

  const tractors = useMemo(
    () => equipments.filter((e) => e.equipmentType === 'TRACTOR'),
    [equipments]
  )
  const trailers = useMemo(
    () => equipments.filter((e) => e.equipmentType === 'TRAILER'),
    [equipments]
  )

  const rows = tab === 'TRACTOR' ? tractors : trailers

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={tab}
        onChange={(_, v: EquipmentType) => setTab(v)}
        sx={{
          minHeight: 36,
          mb: 1,
          borderBottom: `1px solid ${BORDER}`,
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0.5,
            px: 0,
            mr: 2.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            color: MUTED,
          },
          '& .Mui-selected': { color: ACCENT_BLUE },
          '& .MuiTabs-indicator': { bgcolor: ACCENT_BLUE, height: 2 },
        }}
      >
        <Tab label="Tractors" value="TRACTOR" />
        <Tab label="Trailers" value="TRAILER" />
      </Tabs>

      <TableContainer sx={{ maxHeight: 130, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Equipment #', 'Type', 'Status'].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: MUTED,
                    bgcolor: '#FAFBFC',
                    borderBottom: `1px solid ${BORDER}`,
                    py: 1,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ py: 4, color: MUTED, fontSize: 13 }}>
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ py: 4, color: MUTED, fontSize: 13 }}>
                  No {tab === 'TRACTOR' ? 'tractors' : 'trailers'} found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row: EquipmentResponse) => (
                <EquipmentRow key={row.equipmentId} row={row} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ pt: 1 }}>
        <Button
          component={RouterLink}
          to="/equipment"
          size="small"
          sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT_BLUE, fontSize: 13, p: 0 }}
        >
          View all {tab === 'TRACTOR' ? 'tractors' : 'trailers'}
        </Button>
      </Box>
    </Box>
  )
}

function EquipmentRow({ row }: { row: EquipmentResponse }) {
  return (
    <TableRow hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
      <TableCell
        sx={{ fontSize: 13, fontWeight: 600, color: '#111827', borderBottom: `1px solid ${BORDER}`, py: 0.75 }}
      >
        {row.unitNumber}
      </TableCell>
      <TableCell sx={{ fontSize: 13, color: '#374151', borderBottom: `1px solid ${BORDER}`, py: 0.75 }}>
        {formatType(row.equipmentType)}
      </TableCell>
      <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, py: 0.75 }}>
        <Chip
          label="Available"
          size="small"
          sx={{
            height: 24,
            fontSize: 12,
            fontWeight: 600,
            color: GREEN,
            bgcolor: alpha(GREEN, 0.12),
            border: `1px solid ${alpha(GREEN, 0.35)}`,
          }}
        />
      </TableCell>
    </TableRow>
  )
}

export default FleetEquipmentPanel
